import { CommonModule } from '@angular/common';
import { Component, effect } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivityAssigneeService } from '../../../shared-services/activity-assignee.service';
import { AbstractAssigneeFilterInputComponent } from '../../abstract-custom-filters/abstract-assignee/abstract-assignee-filter-input.component';
import { UserWithDisplayName } from '../../../tokens-and-interfaces';
import { userFilterFn } from '../../../utils';

@Component({
  template: `
    <glxy-form-field bottomSpacing="none">
      <input
        [placeholder]="'TABLE_FILTERS.SELECT_EXPRESSION_PLACEHOLDER' | translate"
        type="text"
        matInput
        [formControl]="formControl"
        [matAutocomplete]="auto"
        data-testid="filter-user-input"
      />
      <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn(availableOptions())">
        <mat-option *ngFor="let option of filteredOptions()" [value]="option">
          {{ option?.displayName }}
        </mat-option>
      </mat-autocomplete>
    </glxy-form-field>
  `,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
  ],
})
export class UserFilterInputComponent extends AbstractAssigneeFilterInputComponent<UserWithDisplayName> {
  protected override emitSearchTermValue = false;

  filterFn(user: UserWithDisplayName, searchTerm: string): boolean {
    return userFilterFn(user, searchTerm);
  }
  extractFilterTerm(user: string | UserWithDisplayName): string {
    return typeof user === 'string' ? user : user?.userId;
  }
  getId(user: UserWithDisplayName): string {
    return user.userId;
  }
  getDisplayName(user: UserWithDisplayName): string {
    return user.displayName;
  }
  setAvailableOptions(): void {
    this.userService.allUsers$.pipe(takeUntilDestroyed()).subscribe((allUsers) => this.availableOptions.set(allUsers));
  }

  constructor(private readonly userService: ActivityAssigneeService) {
    super();
    this.setAvailableOptions();

    effect(() => {
      const value = this.searchTerm();
      if (!value) {
        this.valueChanges.emit([]);
      }
      if (typeof value === 'string') {
        // never emit the input value directly. search the user and set it to formControl
        this.userService.searchTerm$$.next(value);
        const availableOptions = this.availableOptions();
        const user = availableOptions.find((o) => o.userId === value);
        if (user) {
          this.formControl.setValue(user);
        } else {
          this.valueChanges.emit([]);
        }
        return;
      }
      const userId = this.getId(value);
      if (userId) {
        return this.valueChanges.emit([{ string: userId }]);
      }
    });
  }
}
